import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  computed,
  signal,
  inject,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { EdeskService } from '../../core/edesk.service';
import { GetFoldersResponse } from '../../api/model/getFoldersResponse';
import { Folder } from '../../api/model/folder';
import { AuthService } from '../../core/auth/auth.service';

type FolderNode = Folder & { children?: FolderNode[] };

@Component({
  selector: 'app-folders',
  standalone: true,
  imports: [CommonModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './folders.component.html',
  styles: [
    `
      .app-folders {
        margin: 0;
        padding-left: 0;
      }
      .app-folder {
        list-style: none;
        margin: 4px 0;
      }
      .app-folder-icon {
        vertical-align: middle;
        margin-right: 8px;
        display: inline-block;
      }
      .app-folders-error {
        display: flex;
        align-items: flex-start;
        gap: 8px;
        padding: 10px 12px;
        background: #fdf0ef;
        border-left: 4px solid #d4351c;
        border-radius: 2px;
        font-size: 0.875rem;
        color: #0b0c0c;
        line-height: 1.4;
        margin-bottom: 12px;
      }
    `,
  ],
})
export class FoldersComponent implements OnInit {
  private authService = inject(AuthService);

  @Input() login?: string;
  @Input() selectedFolderId: number | null = null;
  private static readonly DEMO_LOGIN = '9811026148';

  @Output() folderSelected = new EventEmitter<{id: number, name: string}>();
  @Output() composeRequested = new EventEmitter<void>();

  readonly folders = signal<Folder[]>([]);
  loading = signal(false);
  error = signal<string | null>(null);
  filter = signal('');

  /** Build tree once whenever folders() changes */
  private tree = computed<FolderNode[]>(() => {
    const input = this.folders() ?? [];

    // idFolderParent may arrive as a plain number OR as a JsonNullable wrapper
    // object {value: number} depending on backend serialization config
    const resolveParentId = (f: Folder): number | null => {
      const raw = f.idFolderParent as any;
      if (raw == null) return null;
      if (typeof raw === 'number') return raw;
      if (typeof raw === 'object' && raw.value != null) return raw.value;
      return null;
    };

    const byId = new Map<number, FolderNode>();
    input.forEach((f) => byId.set(f.idFolder, { ...f, children: [] }));

    const roots: FolderNode[] = [];
    input.forEach((f) => {
      const node = byId.get(f.idFolder)!;
      const pid = resolveParentId(f);
      if (pid == null) {
        roots.push(node);
      } else {
        const parent = byId.get(pid);
        if (parent) parent.children!.push(node);
        else roots.push(node);
      }
    });

    // Add custom "Rozpracované" (Drafts) folder at the end
    const draftsFolder: FolderNode = {
      idFolder: -1, // Special ID for drafts
      name: 'Rozpracované',
      systemFolder: false,
      children: []
    };
    roots.push(draftsFolder);

    return roots;
  });

  /** Text filter applied to the tree */
  filteredTree = computed(() => {
    const stripHidden = (nodes: FolderNode[]): FolderNode[] =>
      nodes
        .filter((n) => !this.isDraftFolder(n))
        .map((n) => ({ ...n, children: stripHidden(n.children ?? []) }));

    const visibleTree = stripHidden(this.tree());

    const q = this.filter().trim().toLocaleLowerCase();
    if (!q) return visibleTree;

    const match = (n: FolderNode) =>
      this.displayFolderName(n).toLocaleLowerCase().includes(q);
    const dfs = (nodes: FolderNode[]): FolderNode[] =>
      nodes
        .map((n) => {
          const kids = dfs(n.children ?? []);
          if (match(n) || kids.length) return { ...n, children: kids };
          return null;
        })
        .filter(Boolean) as FolderNode[];

    return dfs(visibleTree);
  });

  filteredCount = computed(() => {
    const count = (nodes: FolderNode[]): number =>
      nodes.reduce((acc, n) => acc + 1 + count(n.children ?? []), 0);
    return count(this.filteredTree());
  });

  constructor(private api: EdeskService) {}

  ngOnInit(): void {
    this.reload();
  }

  private effectiveLogin(): string {
    // Pouzit login z tokenu (sub) alebo fallback
    const tokenLogin = this.authService.getUserLogin();
    const result = this.login ?? tokenLogin ?? FoldersComponent.DEMO_LOGIN;
    console.log('Loading folders for login:', result);
    return result;
  }

  reload(): void {
    this.loading.set(true);
    this.error.set(null);
    this.folders.set([]); // clear stale folders immediately

    this.api.getFolders().subscribe({
      next: (res: GetFoldersResponse) => {
        this.folders.set(res.folders ?? []);
        this.loading.set(false);
        
        // Auto-select Inbox (Prijaté) as default if no folder is selected
        if (this.selectedFolderId === null && res.folders && res.folders.length > 0) {
          const inbox = res.folders.find(f => 
            f.systemFolder === true && f.name?.toLowerCase() === 'inbox'
          );
          if (inbox) {
            this.pick(inbox);
          }
        }
      },
      error: (err) => {
        console.error('getFolders failed', err);
        const status = err?.status;
        if (status === 502 || status === 500) {
          this.error.set('Priečinky nie sú dostupné. Používateľ nemá nastavené zastupovanie pre túto schránku.');
        } else if (status === 403) {
          this.error.set('Nemáte oprávnenie na prístup k tejto schránke.');
        } else {
          this.error.set('Nepodarilo sa načítať priečinky.');
        }
        this.loading.set(false);
      },
    });
  }

  onFilterInput(evt: Event): void {
    const value = (evt.target as HTMLInputElement | null)?.value ?? '';
    this.filter.set(value);
  }

  pick(f: Folder): void {
    if (f?.idFolder != null) {
      this.folderSelected.emit({
        id: f.idFolder,
        name: this.displayFolderName(f)
      });
    }
  }

  trackById(index: number, f: Folder): number | string {
    return f.idFolder ?? f.name ?? index;
  }

  isSelected(f: Folder): boolean {
    return (f as any)?.idFolder === this.selectedFolderId;
  }

  isDraftFolder(f: Folder): boolean {
    return f?.systemFolder === true && (f?.name || '').toLowerCase() === 'drafts';
  }

  displayFolderName(f: Folder): string {
    if (!f?.systemFolder) return f?.name || '';

    switch ((f?.name || '').toLowerCase()) {
      case 'inbox':
        return 'Prijaté';
      case 'sentitems':
        return 'Odoslané';
      case 'bin':
        return 'Kôš';
      default:
        return f?.name || '';
    }
  }

  // Show a count if your DTO has some form of count (works with multiple backends)
  displayCount(f: unknown): number | null {
    const x = f as any;
    if (typeof x?.Count === 'number') return x.Count;
    if (typeof x?.MessageCount === 'number') return x.MessageCount;
    if (typeof x?.Total === 'number') return x.Total;
    return null;
  }

  // If you built a tree view-model externally, expose children safely
  childrenOf(f: unknown): any[] {
    const x = f as any;
    return Array.isArray(x?.children) ? x.children : [];
  }

  openCompose() {
    this.composeRequested.emit();
  }

  /**
   * Returns the appropriate icon path for each folder type
   */
  getFolderIconPath(f: Folder): string {
    if (!f?.systemFolder) {
      // Regular subfolder - folder icon
      return 'assets/icons/folder.svg';
    }

    switch ((f?.name || '').toLowerCase()) {
      case 'inbox':
        return 'assets/icons/mail.svg';
      case 'sentitems':
        return 'assets/icons/outbox.svg';
      case 'drafts':
        return 'assets/icons/draft.svg';
      case 'bin':
        return 'assets/icons/bin.svg';
      default:
        return 'assets/icons/folder.svg';
    }
  }
}
